import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { COLLECTIONS } from '@/config/constants';
import { Bell, ExternalLink, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

/**
 * Notification Click Page — replicates old app-notification-click.html.
 *
 * Reads query params: notificationId, appCode, notificationTitle, notificationContent
 * Fetches the notification data doc from Firestore, builds the correct CRM record URL,
 * and redirects the user to it.
 */

function buildCrmLink(
  extensionName: string,
  notifId: string,
  docData: Record<string, string>
): string {
  let linkToOpen = docData.linkToOpen || '';
  const domain = docData.targetDomain || '';
  const entityModule = docData.entityModule || '';
  const entityId = docData.entityId || '';

  if (extensionName.includes('forzohocrm') && domain) {
    const tld = domain.split('.').slice(2).join('.') || 'com';
    linkToOpen = `https://crm.zoho.${tld}/crm/tab/${entityModule}/${entityId}`;
  }

  if (extensionName.includes('forhubspotcrm') && domain) {
    const orgId = notifId ? notifId.split('___')[0] : '';
    const mod = entityModule.substring(0, entityModule.length - 1).toLowerCase();
    linkToOpen = `https://app.hubspot.com/contacts/${orgId}/${mod}/${entityId}`;
  }

  if (extensionName.includes('forpipedrive') && domain) {
    linkToOpen = `https://${domain}/${entityModule}/${entityId}`;
  }

  if (extensionName.includes('forbitrix24') && domain) {
    linkToOpen = `https://${domain}/crm/${entityModule}/details/${entityId}/`;
  }

  if (extensionName.includes('forfreshworkscrm') && domain) {
    const mod = entityModule === 'sales_account' ? 'account' : entityModule;
    linkToOpen = `https://${domain}/crm/sales/${mod}s/${entityId}`;
  }

  if (extensionName.includes('forzendesksell') && domain) {
    const pathRef =
      entityModule === 'contact'
        ? '/crm'
        : entityModule === 'deal'
          ? '/sales'
          : '';
    linkToOpen = `https://app.futuresimple.com${pathRef}/${entityModule}s/${entityId}`;
  }

  if ((extensionName.includes('forsalesporcecrm') || extensionName.includes('forsalesforcecrm')) && domain) {
    const base = domain.split('.my.salesforce.com')[0];
    linkToOpen = `https://${base}.lightning.force.com/lightning/r/${entityModule}/${entityId}/view`;
  }

  if (extensionName.includes('forzohodesk') && domain && docData.webUrl) {
    linkToOpen = docData.webUrl;
  }

  if (extensionName.includes('forzohobigin') && domain) {
    const dcExt = domain.split('.').slice(2).join('.');
    linkToOpen = `https://bigin.zoho.${dcExt}/bigin/Home#/${entityModule.toLowerCase()}/${entityId}`;
  }

  if (extensionName.includes('forleadsquared') && domain) {
    const dcExt = domain.split('api-')[1] || domain;
    linkToOpen = `https://${dcExt}/LeadManagement/LeadDetails?LeadID=${entityId}`;
  }

  if (extensionName.includes('forzohorecruit') && domain) {
    linkToOpen = `https://${domain}/recruit/EntityInfo.do?id=${entityId}&module=${entityModule}&submodule=${entityModule}`;
  }

  if (extensionName.includes('forfreshdesk') && domain) {
    linkToOpen = `https://${domain}/a/${entityModule}/${entityId}`;
  }

  if (extensionName.includes('formonday') && domain) {
    linkToOpen = `https://${domain}/boards/${entityModule}/pulses/${entityId}`;
  }

  if (extensionName.includes('forzendesksupport') && domain) {
    linkToOpen = `https://${domain}/agent/${entityModule}/${entityId}`;
  }

  return linkToOpen;
}

function formatAppName(extensionName: string): string {
  const known: Record<string, string> = {
    twilioforzohocrm: 'Twilio for Zoho CRM',
    twilioforhubspotcrm: 'Twilio for HubSpot',
    twilioforbitrix24: 'Twilio for Bitrix24',
    whatsappforzohocrm: 'WhatsApp for Zoho CRM',
    whatsappforhubspotcrm: 'WhatsApp for HubSpot',
    calendlyformonday: 'Calendly for Monday.com',
  };
  if (known[extensionName]) return known[extensionName];
  const parts = extensionName.split('for');
  if (parts.length >= 2) {
    const tpa = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const saas = parts.slice(1).join('for');
    const saasFormatted = saas.charAt(0).toUpperCase() + saas.slice(1);
    return `${tpa} for ${saasFormatted}`;
  }
  return extensionName;
}

/** Extract a short hostname from a full URL */
function shortDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function NotificationClickPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'error' | 'redirecting'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const retriesRef = useRef(0);
  const processedRef = useRef(false);

  const appCode = searchParams.get('appCode') || '';
  const notificationId = searchParams.get('notificationId') || '';
  const notificationTitle = searchParams.get('notificationTitle') || '';
  const notificationContent = searchParams.get('notificationContent') || '';

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    if (!appCode || !notificationId) {
      setStatus('error');
      setErrorMsg('Missing appCode or notificationId in URL.');
      return;
    }

    fetchAndRedirect();

    async function fetchAndRedirect() {
      try {
        const notifDoc = doc(
          db,
          COLLECTIONS.NOTIFICATION_DATA,
          appCode,
          'notificationDataMap',
          notificationId
        );
        const snap = await getDoc(notifDoc);

        if (snap.exists()) {
          const docData = { id: snap.id, ...snap.data() } as Record<string, string>;
          const link = buildCrmLink(appCode, notificationId, docData);

          if (link) {
            setStatus('redirecting');
            setRedirectUrl(link);
            // Small delay so user sees the redirect state
            setTimeout(() => {
              window.location.href = link;
            }, 600);
          } else {
            setStatus('error');
            setErrorMsg('No redirect link found in notification data.');
          }
        } else {
          retriesRef.current++;
          setRetryCount(retriesRef.current);
          if (retriesRef.current > 5) {
            setStatus('error');
            setErrorMsg('Notification redirect data not found. The record may not be available yet.');
          } else {
            setTimeout(fetchAndRedirect, 1500);
          }
        }
      } catch (err) {
        console.error('Notification click error:', err);
        setStatus('error');
        setErrorMsg('Failed to fetch notification data.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const appName = formatAppName(appCode);
  const decodedTitle = notificationTitle ? decodeURIComponent(notificationTitle) : '';
  const decodedContent = notificationContent ? decodeURIComponent(notificationContent) : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="rounded-2xl border border-border bg-card shadow-lg overflow-hidden">
          {/* Top accent bar */}
          <div className="h-1 bg-gradient-to-r from-primary via-primary/70 to-primary/40" />

          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Bell className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Notification
                </p>
                <p className="text-sm font-semibold text-foreground">{appName}</p>
              </div>
            </div>
          </div>

          {/* Notification content */}
          {(decodedTitle || decodedContent) && (
            <div className="mx-6 mb-4 rounded-xl bg-muted/50 border border-border/50 p-4">
              {decodedTitle && (
                <p className="text-sm font-semibold text-foreground leading-snug">
                  {decodedTitle}
                </p>
              )}
              {decodedContent && (
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                  {decodedContent}
                </p>
              )}
            </div>
          )}

          {/* Status section */}
          <div className="px-6 pb-6">
            {/* Loading */}
            {status === 'loading' && (
              <div className="flex flex-col items-center py-4 space-y-3">
                <div className="relative">
                  <div className="h-12 w-12 rounded-full border-2 border-muted" />
                  <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground">
                    Looking up record...
                  </p>
                  {retryCount > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Attempt {retryCount + 1} of 6
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Redirecting */}
            {status === 'redirecting' && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 py-3">
                  <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                    <ArrowRight className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      Opening your record
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {shortDomain(redirectUrl)}
                    </p>
                  </div>
                </div>

                {/* Progress bar animation */}
                <div className="h-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full animate-pulse"
                    style={{ width: '80%', transition: 'width 0.6s ease' }}
                  />
                </div>

                <a
                  href={redirectUrl}
                  className="flex items-center justify-center gap-2 w-full rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open manually
                </a>
              </div>
            )}

            {/* Error */}
            {status === 'error' && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-xl bg-destructive/5 border border-destructive/20 p-4">
                  <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-destructive">
                      Unable to redirect
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {errorMsg}
                    </p>
                  </div>
                </div>

                <a
                  href={`/send-sms?appCode=${appCode}`}
                  className="flex items-center justify-center gap-2 w-full rounded-xl bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Open App
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Footer branding */}
        <p className="text-center text-[11px] text-muted-foreground/60 mt-4">
          Powered by Ulgebra
        </p>
      </div>
    </div>
  );
}
